import throttle from 'lodash.throttle';
import { gsap } from "gsap";
import Menu from './Menu';

export default class Header {

    constructor() {

		this.breakpoint = window.matchMedia('(min-width:992px)');

		this.elm = document.querySelector('[data-header]');
        if(!this.elm){
            return;
        }

        this.init();


		this.prevScroll = 0;
        this.scrollDiffUp = 0;
		this.scrollDiffDown = 0;
		document.addEventListener(
			'scroll',
			throttle(e => this.onScroll(), 100),
		);

        window.addEventListener('resize', () => {
			this.onResize();
		});
		this.onResize();

        document.addEventListener('hide-breadcrumb', () => {
            this.hideBreadcrumb();
        });

        document.addEventListener('bless', (e) => {
			this.checkTemplate(e);
            this.updateBreadcrumb(e);
        });

		document.addEventListener('hide-header', () => {
			this.hideHeader();
		});

		document.addEventListener('show-header', () => {
			this.showHeader();
		});
    }

    init(container) {
        this.menuOpen = false;

		this.menuDuration = 1.2;
		this.menuDelay = 0.4;

        this.main = this.elm.querySelector('[data-main]');
        this.logo = this.elm.querySelector('[data-logo]');
		this.logoSvg = this.logo.querySelector('svg');
        this.sub = this.elm.querySelector('[data-sub]');
        this.breadcrumb = this.elm.querySelector('[data-breadcrumb]');
		this.subBackground = this.elm.querySelector('[data-header-background]');

		this.nav = this.elm.querySelector('[data-nav]');
        this.menu = new Menu(this.nav);

		this.footer = this.elm.querySelector('[data-header-footer]');

		this.headerProgramLink = this.elm.querySelector('[data-header-program-link]');


        this.overlay = this.elm.querySelector('[data-overlay]');
        this.overlay.addEventListener('click', () => {
            this.closeMenu();
        });

		this.wantsToClose = false;
        document.addEventListener('close-menu', () => {
            if(this.menuOpen) {
			    this.closeMenu();
            }
		});

        document.addEventListener('show-program-link-in-header', () => {
            this.elm.classList.add('c-header--show-program-link');
		});

        document.addEventListener('hide-program-link-in-header', () => {
			this.elm.classList.remove('c-header--show-program-link');
		});

		this.navButton = this.elm.querySelector('[data-menu-toggle]');
		if(this.navButton) {
			this.navButton.addEventListener('click', () => {
				this.menuOpen ? this.closeMenu() : this.openMenu();
			});
		}


        this.setKeyboardFocusableElements();

		const mm = gsap.matchMedia()
		mm.add({
			isMobile: "(max-width: 991px)",
			isDesktop: "(min-width: 992px)",
			isLarge: "(min-width: 1400px)"
		}, (context) => {
			this.updateStaticValues(context);
			this.menuOpenTimeline = this.createMenuOpenTimeline(context);
			this.menuCloseTimeline = this.createMenuCloseTimeline(context);
		});
    }

    setKeyboardFocusableElements() {
        this.getKeyboardFocusableElements = [];
		this.menuElm = this.nav.querySelector('.c-menu');
		this.topLevelItems = this.menuElm.children;
		[...this.topLevelItems].forEach(topItem => {
			const children = topItem.children;
			if(children.length) {
				this.getKeyboardFocusableElements.push(children[0]);
			}
		});

		const footerLink = this.elm.querySelector('[data-menu-footer-link]');
		if(footerLink) {
			this.getKeyboardFocusableElements.push(footerLink);
		}

        this.getKeyboardFocusableElements.forEach((element) => (element.tabIndex = -1));

        this.firstFocusableEl = this.getKeyboardFocusableElements[0];
		this.lastFocusableEl = this.getKeyboardFocusableElements[this.getKeyboardFocusableElements.length - 1];
    }

	updateStaticValues(context) {
		if(this.menuAnimating) {
			this.menuOpenTimeline.pause();
			this.menuCloseTimeline.pause();
			this.menuAnimating = false;
		}

		// Set initial transforms with gsap for better performance
		let { isMobile, isDesktop, isLarge } = context.conditions;

		this.setMobileLogoScale();

		if(this.menuOpen) { // If Menu is Open

			if(isMobile) {

				gsap.set(this.navButton, {
					y: 5
				});

			}

			if(isDesktop) {

				gsap.set(this.logoSvg, {
					y: 10,
					scale: 1,
				});

				gsap.set(this.subBackground, {
					yPercent: 0,
					y: 0,
				});

				gsap.set(this.main, {
					clearProps: "all"
				});
				gsap.set(this.sub, {
					clearProps: "all"
				});

			}

			gsap.set(this.overlay, {
				opacity: 1,
			});
			gsap.set(this.nav, {
				opacity: 1,
			});
			gsap.set(this.footer, {
				opacity: 1,
			});

		} else { // If Menu is Closed

			if(isMobile) {

				gsap.set(this.navButton, {
					y: 0
				});

			}

			if(isDesktop) {

				let scaleFactor = 1/3.5;
				if(isLarge) {
					scaleFactor = 1/5.5;
				}
				gsap.set(this.logoSvg, {
					y: 0,
					scale: scaleFactor,
				});
				gsap.set(this.subBackground, {
					y: 63, // Header height,
					yPercent: -100,
				});
				gsap.set(this.main, {
					clearProps: "all"
				});
				gsap.set(this.sub, {
					clearProps: "all"
				});
			}

			gsap.set(this.overlay, {
				opacity: 0,
			});
			gsap.set(this.nav, {
				opacity: 0,
			});
			gsap.set(this.footer, {
				opacity: 0,
			});

		}

	}

	setMobileLogoScale() {
		if(this.breakpoint.matches) { // Desktop
			return;
		}
		if(this.menuOpen) { // If Menu is Open

			gsap.set(this.logoSvg, {
				y: 0,
				scale: 1,
			});
			gsap.set(this.subBackground, {
				yPercent: 0,
				y: () => {
					const fullLogoHeight = this.getFullLogoHeight();
					return window.innerHeight - fullLogoHeight - 28 - 34;
				},
			});
			gsap.set(this.main, {
				y: 0,
			});
			gsap.set(this.sub, {
				y: 1
			});

		} else { // If Menu is Closed

			const fullLogoHeight = this.getFullLogoHeight();
			gsap.set(this.logoSvg, {
				y: fullLogoHeight - (22 - 3),
				scale: 22 / fullLogoHeight,
			});
			gsap.set(this.subBackground, {
				yPercent: 0,
				y: 0,
			});
			gsap.set(this.main, {
				y: () => {
					const fullLogoHeight = this.getFullLogoHeight();
					return (-1 * (fullLogoHeight - ( 22 - 6)));
				}
			});
			gsap.set(this.sub, {
				y: () => {
					const fullLogoHeight = this.getFullLogoHeight();
					return (-1 * (fullLogoHeight - ( 22 - 6)));
				}
			});

		}
	}

	getFullLogoHeight() {
		return ((window.innerWidth-24)*0.15);
	}

	createMenuOpenTimeline(context) {
		let { isMobile, isDesktop, isLarge } = context.conditions;

		// Calculate Target Values
		let yValue = 0;
		if(isDesktop) {
			yValue = 10;
		}

		const tl = gsap.timeline({
			paused: true,
			defaults: {
				duration: this.menuDuration,
				ease: 'power2.inOut',
				overwrite: true
			},
			onComplete: () => {
				this.menuAnimating = false;
				if(this.wantsToClose) {
					this.closeMenu();
				}
			}
		});


		if(isMobile) {

			tl.to(this.navButton, {
				y: 5
			}, 0);

			tl.to(this.logoSvg, {
				y: 0,
				scale: 1,
			}, 0);

			tl.to(this.subBackground, {
				y: () => {
					const fullLogoHeight = this.getFullLogoHeight();
					return window.innerHeight - fullLogoHeight - 28 - 34;
				}
			}, 0);

			tl.to(this.main, {
				y: 0
			}, 0);

			tl.to(this.sub, {
				y: 1
			}, 0);
		}
		if(isDesktop) {

			tl.to(this.logoSvg, {
				y: 10,
				scale: 1,
			}, 0);

			tl.to(this.subBackground, {
				yPercent: 0,
				y: 0,
			}, 0);

		}

		tl.to(this.overlay, {
			opacity: 1,
			ease: 'none',
			onComplete: () => {
				this.elm.classList.add('is-opened');
			}
		}, 0);

		tl.to(this.nav, {
			opacity: 1,

			duration: this.menuDelay*2,
			ease: 'none',
		}, this.menuDuration);

		tl.fromTo(this.footer, {
			opacity: 0
		}, {
			opacity: 1,
			duration: this.menuDelay*2,
			ease: 'none',
		}, this.menuDuration+this.menuDelay);

		return tl;
	}

	createMenuCloseTimeline(context) {
		let { isMobile, isDesktop, isLarge } = context.conditions;

		let scaleFactor = 1;
		if(isDesktop) {
			scaleFactor = 1/3.5;
		}
		if(isLarge) {
			scaleFactor = 1/5.5;
		}

		const tl = gsap.timeline({
			paused: true,
			defaults: {
				duration: this.menuDuration,
				ease: 'power2.inOut',
			},
			onComplete: () => {
				this.menuAnimating = false;
				this.elm.classList.remove('is-opened');
			}
		});

		if(isMobile) {

			// static
			tl.to(this.subBackground, {
				y: 0,
			}, 0);

			tl.to(this.navButton, {
				y: 0
			}, 0);


			// on Resize
			tl.to(this.logoSvg, {
				scale: () => {
					const fullLogoHeight = this.getFullLogoHeight();
					return 22 / fullLogoHeight;
				},
				y: () => {
					const fullLogoHeight = this.getFullLogoHeight();
					return fullLogoHeight - (22 - 3);
				}
			}, 0);
			tl.to(this.main, {
				y: () => {
					const fullLogoHeight = this.getFullLogoHeight();
					return (-1 * (fullLogoHeight - ( 22 - 6)));
				}
			}, 0);

			tl.to(this.sub, {
				y: () => {
					const fullLogoHeight = this.getFullLogoHeight();
					return (-1 * (fullLogoHeight - ( 22 - 6)));
				}
			}, 0);


		}

		if(isDesktop) {

			let scaleFactor = 1/3.5;
			if(isLarge) {
				scaleFactor = 1/5.5;
			}

			tl.to(this.logoSvg, {
				scale: scaleFactor,
				y: 0
			}, 0);

			tl.to(this.subBackground, {
				yPercent: -100,
				y: 63, // Header height

				duration: this.menuDuration,
			}, this.menuDelay);
		}

		tl.to(this.footer, {
			opacity: 0,
			duration: this.menuDelay,
			ease: 'none',
		}, 0);

		tl.to(this.nav, {
			opacity: 0,
			duration: this.menuDelay,
		}, 0);

		tl.to(this.overlay, {
			opacity: 0,
			ease: 'none',
		}, 0);


		return tl;
	}

	openMenu() {
		if(this.menuAnimating||this.menuOpen) {
			return;
		}
		this.menuAnimating = true;
		this.menuOpen = true;

        document.dispatchEvent(new CustomEvent('disable-scroll'));

        this.elm.classList.add('is-open');

		this.navButton.setAttribute('aria-expanded', true);
		this.navButton.setAttribute('aria-label', 'close menu');

		this.hideBreadcrumb();

        this.trapFocus();

		this.menuOpenTimeline.restart();
	}

	closeMenu() {
		this.wantsToClose = true;
		if(this.menuAnimating||!this.menuOpen) {
			return;
		}
		this.menuAnimating = true;
		this.menuOpen = false;
		this.wantsToClose = false;

        this.elm.classList.remove('is-open');

		document.dispatchEvent(new CustomEvent('enable-scroll'));

        gsap.set(this.navButton, {
            right: parseInt(getComputedStyle(this.navButton).right)+'px'
        });

		this.navButton.setAttribute('aria-expanded', false);
		this.navButton.setAttribute('aria-label', 'open menu');

        this.menu.closeSubmenus();

        this.removeTrapFocus();

		this.menuCloseTimeline.restart();
	}

    onScroll() {
		// Header Logo
		if(window.scrollY > 80 && (window.innerHeight + window.scrollY) < (document.body.offsetHeight-62)) {
			if(this.prevScroll>window.scrollY) {

				this.scrollDiffUp = this.scrollDiffUp+(this.prevScroll-window.scrollY);
				if(this.scrollDiffUp>200) {
					document.body.classList.add('body--has-scrolled-up');
					document.body.classList.remove('body--has-scrolled-down');

					this.scrollDiffUp = 0;
				}
			} else {
				this.scrollDiffDown = this.scrollDiffDown+(window.scrollY-this.prevScroll);
				if(this.scrollDiffDown>200) {
					document.body.classList.remove('body--has-scrolled-up');
					document.body.classList.add('body--has-scrolled-down');

					this.scrollDiffDown = 0;
				}
			}
		} else {
			document.body.classList.remove('body--has-scrolled-up');
			document.body.classList.remove('body--has-scrolled-down');

			this.scrollDiffUp = 0;
			this.scrollDiffDown = 0;
		}
		this.prevScroll = window.scrollY;
	}

    onResize() {
        document.documentElement.style.setProperty('--viewWidth', `${window.innerWidth}`);
        document.documentElement.style.setProperty('--viewWidthPx', `${window.innerWidth}px`);

        document.documentElement.style.setProperty('--fullLogoHeight', `${(window.innerWidth-24)*0.15}`);
        document.documentElement.style.setProperty('--fullLogoHeightPx', `${(window.innerWidth-24)*0.15}px`);

		this.setMobileLogoScale();

		document.documentElement.classList.add('resize-set');
		setTimeout(() => {
			document.documentElement.classList.add('transition-set');
		}, 0);
    }

    hideBreadcrumb() {
        gsap.to(this.breadcrumb, {
            opacity: 0,
            duration: 0.5,
            ease: 'none'
        });
    }

    updateBreadcrumb(e) {
        const breadcrumb = e.target.dataset.breadcrumb;
        if(breadcrumb) {
            gsap.to(this.breadcrumb, {
                opacity: 1,
                duration: 0.5,
                ease: 'none',
				delay: this.menuAnimating ? this.menuDuration : 0,
                onStart: () => {
                    this.breadcrumb.innerHTML = breadcrumb;
                }
            });
        }
    }

	checkTemplate(e) {
		const namespace = e.target.dataset.barbaNamespace;
		if(namespace==='home') {
			this.elm.classList.remove('is-visible');
			setTimeout(() => {
				this.elm.classList.add('c-header--home');
			}, 300);
		} else {
			this.elm.classList.remove('c-header--home');
			setTimeout(() => {
				this.elm.classList.add('is-visible');
			}, 0);
		}
	}

    trapFocus() {
		this.getKeyboardFocusableElements.forEach((element) => element.removeAttribute('tabindex'));

		this.tabfocussed = (event) => this.tabFocus(event);
		document.addEventListener('keydown', this.tabfocussed);
	}

	tabFocus(e) {
		if (e.key === 'Escape') {
			this.closeMenu();
			this.navButton.focus();
		}

		const KEYCODE_TAB = 9;
		const isTabPressed = e.key === 'Tab' || e.keyCode === KEYCODE_TAB;

		if (!isTabPressed) {
			return;
		}

		if (e.shiftKey) {
			if (document.activeElement === this.firstFocusableEl) {
				this.lastFocusableEl.focus();
				e.preventDefault();
			}
		} else {
			if (document.activeElement === this.lastFocusableEl) {
				this.firstFocusableEl.focus();
				e.preventDefault();
			}
		}
	}

    removeTrapFocus(element) {
		this.getKeyboardFocusableElements.forEach((element) => (element.tabIndex = -1));
		document.removeEventListener('keydown', this.tabfocussed);
	}

	hideHeader() {
		this.elm.classList.remove('is-visible');
	}

	showHeader() {
		this.elm.classList.add('is-visible');
	}

}